'use client';

import ActivityBanner, { ActivityBannerData } from '@/app/(activity)/components/ActivityBanner';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { BannerData } from '../types';
interface TransitionBannerProps {
  id?: string;
  bannerData: BannerData;
  onBannerClick?: (params: { group_id: string | number }) => void;
  onBannerExp?: (params: { group_id: string | number }) => void;
}

export default function TransitionBanner({
  id,
  bannerData,
  onBannerClick,
  onBannerExp,
}: TransitionBannerProps) {
  // 曝光监听
  const [bannerRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 转换数据格式以适配通用ActivityBanner - 使用useMemo缓存
  const activityBannerData: ActivityBannerData = useMemo(
    () => ({
      image: bannerData.image,
      type: bannerData.type,
      group: bannerData.group,
      alt: 'zeelool transition lenses',
    }),
    [bannerData.image, bannerData.type, bannerData.group],
  );

  // Banner曝光埋点处理
  useEffect(() => {
    if (inView && onBannerExp && bannerData.group) {
      onBannerExp({ group_id: bannerData.group });
    }
  }, [inView, onBannerExp, bannerData.group]);

  const handleBannerClick = useCallback(
    (data: ActivityBannerData) => {
      // 处理点击事件和埋点
      if (onBannerClick && data.group) {
        onBannerClick({ group_id: data.group });
      }
    },
    [onBannerClick],
  );

  // 根据Vue版本调整的内容布局 - 使用useMemo缓存
  const bannerContent = useMemo(
    () => (
      <div className="absolute inset-0 w-full h-full pointer-events-none z-[5]">
        {/* 移动端布局 - 底部居中 */}
        <WapOnly>
          <div className="absolute bottom-[32px] left-0 w-full px-[25px] flex flex-col items-center text-center pointer-events-auto">
            {bannerData.title_img && (
              <Image
                src={bannerData.title_img}
                alt={bannerData.title}
                width={135}
                height={80}
                className="w-[135px] h-auto"
              />
            )}
            {bannerData.title && (
              <h3 className="text-white text-[24px] leading-[30px] font-bb mt-[10px] mb-[20px]">
                {bannerData.title}
              </h3>
            )}
            {/* <Link href="/">
            </Link> */}
            {/* <Image
              src="/activity/transition-logo-m.png"
              alt=""
              width={100}
              height={48}
              className="mb-[24px]"
            /> */}
            <MagicJump item={bannerData?.logo?.jump}>
              <Image
                src={bannerData?.logo?.image}
                alt={bannerData.title}
                width={160}
                height={76}
                className="mb-[40px] z-[1]"
              />
            </MagicJump>

            {bannerData.btn_word && (
              <MagicJump
                item={
                  bannerData?.group
                    ? { jump_type: 1, Jump_target: bannerData?.group }
                    : { jump_type: '', Jump_target: '' }
                }
              >
                <div className="w-[254px] h-[36px] bg-white rounded-[18px] text-center leading-[36px] text-[14px] text-primary-black font-bb cursor-pointer">
                  {bannerData.btn_word}
                </div>
              </MagicJump>
            )}
          </div>
        </WapOnly>

        {/* PC端布局 - 左侧垂直居中 */}
        <WebOnly>
          <div className="absolute top-0 left-[3vw] xl:left-[48px] w-[60vw] xl:w-[990px] h-full flex flex-col justify-center items-start text-left pointer-events-auto">
            {bannerData.title_img && (
              <Image
                src={bannerData.title_img}
                alt={bannerData.title}
                width={269}
                height={160}
                className="w-[18vw] xl:w-[269px] h-auto"
              />
            )}
            {bannerData.title && (
              <h3 className="text-white text-[3.5vw] xl:text-[56px] leading-[4.2vw] xl:leading-[67px] font-bb mt-[1vw] xl:mt-[16px] mb-[3vw] xl:mb-[32px]">
                {bannerData.title}
              </h3>
            )}
            {/* <Link href="/">
            </Link> */}
            {/* <Image
              src="/activity/transition-logo-pc.png"
              alt=""
              width={160}
              height={76}
              className="mb-[40px]"
            /> */}
            <MagicJump item={bannerData?.logo?.jump}>
              <Image
                src={bannerData?.logo?.image}
                alt={bannerData.title}
                width={160}
                height={76}
                className="mb-[40px] z-[1]"
              />
            </MagicJump>
            {bannerData.btn_word && (
              <MagicJump
                item={
                  bannerData?.group
                    ? { jump_type: 1, Jump_target: bannerData?.group }
                    : { jump_type: '', Jump_target: '' }
                }
              >
                <div className="w-[20vw] xl:w-[324px] h-[3.5vw] xl:h-[56px] bg-white rounded-[28px] text-center leading-[3.5vw] xl:leading-[56px] text-[1vw] xl:text-[16px] text-primary-black font-bb cursor-pointer">
                  {bannerData.btn_word}
                </div>
              </MagicJump>
            )}
          </div>
        </WebOnly>
      </div>
    ),
    [bannerData.title_img, bannerData.title, bannerData.btn_word],
  );

  return (
    <div ref={bannerRef} className="relative">
      <ActivityBanner
        id={id}
        data={activityBannerData}
        height="90vh" // 使用全屏高度
        onBannerClick={handleBannerClick}
      />
      {bannerContent}
    </div>
  );
}
